import { interval } from 'rxjs';
import { Component, inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { genTinyGUID, SawDust } from '@cudirect/shared-ui-library';
import { DecisionStatus } from '@workflow/ecp-shared/enum/enum';
import { ApiResult } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.types';
import {
  Product,
  MembershipService,
  APMembershipOptions,
  ApplicantProduct,
  ShareResponse,
  AdditionalAccountsCardState
} from '@workflow/ecp-workflow/dynamic-app/types/accounts-and-services.type';
import { OaoIncludedAccountComponent } from '@workflow/ecp-workflow/oao/components/oao-included-account/oao-included-account.component';
// eslint-disable-next-line max-len
import { OaoRetirementAccountComponent } from '@workflow/ecp-workflow/oao/components/oao-retirement-account/oao-retirement-account.component';
// eslint-disable-next-line max-len
import { OaoShareCertificatesAccountComponent } from '@workflow/ecp-workflow/oao/components/oao-share-certificates-account/oao-share-certificates-account.component';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';

/** Share Dividend Type */
const SHARE_DIVIDEND_TYPE = 'ShareDividendType';
/** Share Disbursement Type */
const SHARE_DISBURSEMENT_TYPE = 'ShareDisbursementType';
/** Share Contribution Source */
const SHARE_CONTRIBUTION_SOURCE = 'ShareContributionSource';
/** Shares & Services Picklist Path */
const SHARE_SERVICES_PICKLIST_PATH = 'ss:oao-accounts-and-services-picklist';
/** Share Cert Type */
const SHARE_CERT_TYPE = ['retiresharecertificates', 'sharecertificates'];
/** Page alert element id */
const PAGE_ALERT = 'alert-';

/** component to show available accounts and services */
@BuildingBlock('accounts-and-services')
@Component({
  selector: 'ecp-accounts-and-services',
  styleUrls: ['./accounts-and-services.component.scss'],
  templateUrl: './accounts-and-services.component.html'
})
export class AccountsAndServicesComponent extends BuildingBlockControlBaseComponent implements OnInit, OnDestroy {
  protected logger = inject(SawDust).withContext({ component: 'AccountsAndServicesComponent' });

  /** Retirement Savings Child Component  */
  @ViewChild(OaoIncludedAccountComponent) private includedComponent: OaoIncludedAccountComponent;
  @ViewChild(OaoRetirementAccountComponent) private retirementComponent: OaoRetirementAccountComponent;
  /** Share Certificate Child Component  */
  @ViewChildren(OaoShareCertificatesAccountComponent) private sharedCertificateComponent: QueryList<OaoShareCertificatesAccountComponent>;
  /** Accounts & Service Completion path */
  public COMPLETION_STATUS_PATH = 'api:oaoMemberInformation/accountsAndServices/isAccountsServiceCompleted';
  /** Page Content Class Name */
  public PAGE_CONTENT_CLASS_NAME = 'dx-page-content';
  /** Products */
  public products: Product[];
  /** Membership Service */
  public membershipServices: MembershipService[];
  /** Dividend and Maturity options */
  public shareOptions: APMembershipOptions;
  /** Grouping Products based on its Type. */
  public groupedProducts = [];
  /** Grouping Membership Services based on its Type. */
  public groupedServices = [];
  /** RetirementPage Validation Pass */
  public isRetirementAccountValid = true;
  /** Shared Accounts & Services Validation Pass */
  public isSharedAccountValid = true;
  /** Shared Retirement Validation pass */
  public isSharedRetirementValid = true;
  public isOptOutSharedAccountValid = true;
  public isOptOutSharedRetirementValid = true;
  /** lender config data */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public lenderConfig: any = {};
  /** show contact page flag */
  public showContactPage: boolean;
  /** Co-Applicant Shares & Services */
  public coApplicantProducts: ApplicantProduct[];
  /** Check accounts & services 1st time loading from L360 call */
  public isInitialLoading = false;
  /** Check accounts & services modal on data loading from L360 call */
  public isShowHangOnLoading = false;

  public isPageSubmitted = false;

  public showTickMark = false;

  public progress = 0;

  public total = 100;

  public curSec = 0;

  public showFullList = true;

  public showmore = '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public objTimer: any;

  public sharesServices = [];

  /** Property to check Mid-App decision enabled or not  */
  public get isMidAppDecisionEnabled(): boolean {
    const bundleValue = this.statefulService.wrap('api:oaoMemberInformation/isMidAppDecisionEnabled').value;
    return typeof bundleValue == 'boolean' ? bundleValue : false;
  }

  public get isAnyShareSelected(): boolean {
    return this.products?.filter((x) => x.isSelected).length > 0;
  }

  /** On Init */
  public ngOnInit(): void {
    this.setLenderData();
    this.showLoading();
    const decisionStatus = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationStatus').value;
    if (decisionStatus && decisionStatus.toString().toLowerCase() === DecisionStatus.Declined) {
      this.displayContactUsPage();
    } else {
      this.products = [];
      this.coApplicantProducts = [];
      this.LoadShareCertificatePicklist();
    }

    /** When user submit the page, focus the first invalid field */
    this.pageSubmitted$.subscribe((value) => {
      this.isPageSubmitted = value;
      if (this.isPageSubmitted) {
        this.focusFirstInvalidField();
      }
    });
  }

  public focusFirstInvalidField(): void {
    const invalidList = [];
    /** Retirement Savings Component Validation */
    if (this.retirementComponent) {
      const retirementSavingsIds = this.retirementComponent.getInvalidFieldIds();
      if (retirementSavingsIds && retirementSavingsIds.length > 0) invalidList.push(...retirementSavingsIds);
    }
    if (!this.isAnyShareSelected) {
      this.focusHelperService.scrollToTopOffset(`${PAGE_ALERT}${this.page.id}`, 0);
      return;
    }

    /** Share Certificates Component Validation */
    if (this.sharedCertificateComponent?.length) {
      this.sharedCertificateComponent.forEach((s) => {
        const shareCertificatesIds = s.getInvalidFieldIds();
        if (shareCertificatesIds && shareCertificatesIds.length > 0) invalidList.push(...shareCertificatesIds);
      });
    }
    if (this.includedComponent) {
      const includedIds = this.includedComponent.getInvalidFields();
      if (includedIds.length > 0) invalidList.push(...includedIds);
    }

    if (invalidList && invalidList.length > 0) this.focusService.setFocus(invalidList[0].toString());
  }

  /**
   * Centralized logging method to avoid duplicate calls
   * @param eventName - Name of the event to log
   * @param params - Parameters to include in the log
   */
  private logEvent(eventName: string, params): void {
    this.applicationInsightsService.logEvent(eventName, params);
    this.logger.logEvent(eventName, params);
  }

  public LoadShareCertificatePicklist(): void {
    const picklistKeys = `${SHARE_DIVIDEND_TYPE},${SHARE_DISBURSEMENT_TYPE},${SHARE_CONTRIBUTION_SOURCE}`;

    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    const logSrc = 'workflow';
    this.logEvent('ecp.ui.picklist', {
      eventState: 'start',
      correlationId: eventPairId,
      source: logSrc,
      keys: picklistKeys
    });
    const pickListTypes = this.l360Service.getPickListTypes(picklistKeys);
    if (pickListTypes?.length > 0) {
      this.statefulService.wrap(SHARE_SERVICES_PICKLIST_PATH).value = JSON.stringify(pickListTypes);

      this.logEvent('ecp.ui.picklist', {
        eventState: 'end',
        correlationId: eventPairId,
        source: logSrc,
        keys: picklistKeys
      });
      // Fetch Accounts & Service by calling API
      this.GetEligibileProductsFromL360();
    }
  }

  /** Get Eligibile Accounts & Service List by calling L360 API */
  public GetEligibileProductsFromL360(): void {
    const eventPairId = genTinyGUID(24);
    // Are these static shares?
    const products = this.l360Service.cachedAccountAndService;

    // Retrieve the user email and application number
    const appNumber = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString ?? 'Unknown';
    const user = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/email').valueString ?? 'Unknown';

    // Log to appinsights start event
    this.logAppInsightsOaoRadEvent(eventPairId, 'start', appNumber, user, null, null, null);

    // If there are already shares and services in cache, just display them
    if (products) {
      // This method will trigger a UEM end event, so we need to be sure the start event happens first
      this.handleEligibilitySuccess(products, eventPairId, appNumber, user, true);
      return;
    }

    // Show hang on model only if mid app decision enabled
    this.showLoading();

    /** Check Data coming from L360 call or taken back from bundle data */
    this.isInitialLoading = appNumber ? false : true;

    if (this.isInitialLoading) this.initiateProgressBar();

    this.statefulService.saveChangedValues();
    // Using one of the common ID's from the bundle to make a GET call
    this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/firstName').load(() => {
      this.l360Service.GetEligibileProductsFromL360().subscribe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (response: ApiResult<ShareResponse>) => {
          if (response.isSuccess) {
            this.l360Service.cachedAccountAndService = response.data;
            this.handleEligibilitySuccess(this.l360Service.cachedAccountAndService, eventPairId, appNumber, user, false);
          } else {
            this.statefulService.wrap(this.COMPLETION_STATUS_PATH).value = false;
            // If any internal server error from L360 api call then direct to oops error page
            if (response?.code?.toLowerCase() === 'internalservererror') {
              this.field.data.value = null;
              // this.spinnerInjector.hide();
              // this.isShowHangOnLoading = false;
              this.hideLoading();
              this.router.navigate(['/oops']);
            } else {
              // this.spinnerInjector.hide();
              // this.isShowHangOnLoading = false;
              this.hideLoading();
            }

            this.handleError(response);
            const errorResponse = `Reason: ${response?.reason || 'Unknown'} - Message: ${response?.message || 'Unknown'}`;
            this.logAppInsightsOaoRadEvent(eventPairId, 'end', appNumber, user, 'error', errorResponse, 'dynamic');
          }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error: HttpErrorResponse) => {
          this.showContactPage = false;
          this.field.data.value = null;
          this.statefulService.wrap(this.COMPLETION_STATUS_PATH).value = false;
          // this.spinnerInjector.hide();
          // this.isShowHangOnLoading = false;
          this.hideLoading();
          this.handleError(error);
          this.logAppInsightsOaoRadEvent(eventPairId, 'end', appNumber, user, 'error', JSON.stringify(error.error), 'dynamic');
        }
      );
    });
  }

  public handleEligibilitySuccess(result: ShareResponse, eventPairId, appNumber, user, isStatic = false): void {
    if (result.applicationStatus?.toLowerCase() === DecisionStatus.Declined) {
      this.field.data.value = null;
      this.statefulService.wrap(this.COMPLETION_STATUS_PATH).value = false;
      // TODO: Do we need these here? Can we use the appNumber and user from above?
      this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').value = result.applicationId;
      this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationStatus').value = result.applicationStatus;
      this.displayContactUsPage();
      // this.spinnerInjector.hide();
      // this.isShowHangOnLoading = false;
      this.hideLoading();
    } else {
      if (!result?.primaryApplicant?.shares?.length) {
        this.statefulService.wrap(this.COMPLETION_STATUS_PATH).value = false;
        // this.isShowHangOnLoading = false;
        this.hideLoading();
        return;
      }
      if (this.objTimer) {
        this.objTimer.unsubscribe();
      }

      this.progress = 90;
      const timer$ = interval(300);
      const sub = timer$.subscribe((sec) => {
        this.progress = this.progress + 1;

        if (sec === 10) {
          sub.unsubscribe();
          this.showTickMark = true;
          // after progress bar reach 100%, we have to display tickmark fraction of seconds
          setTimeout(() => {
            // this.isShowHangOnLoading = false;
            this.hideLoading();
          }, 300);
        }
      });

      if (result.primaryApplicant) {
        this.handlePrimaryApplicant(result);
      }

      if (result.shareOptions) {
        this.handleShareOptions(result);
      }
    }
    const staticShare = isStatic ? 'static' : 'dynamic';
    this.logAppInsightsOaoRadEvent(eventPairId, 'end', appNumber, user, 'success', null, staticShare);
  }

  /**
   * Logs an event in Application Insights, typically called when an account submission process either starts or ends.
   * This method is specifically meant for tracking the status and associated details of each loan submission attempt.
   *
   * @param correlationId - Unique identifier to correlate events in a single transaction.
   * @param eventState - Represents the state of the event, whether it's 'start' or 'end'.
   * @param appNumber - Unique identifier of the account which is being processed.
   * @param user - Identifier for the user triggering this event, could be username or user ID.
   * @param status - Optional parameter that captures the status of the event. It can either be 'success' or 'error'.
   * @param errorDetails - Optional parameter that captures the details of the error, if any.
   *
   * @returns This method doesn't have a return.
   */
  private logAppInsightsOaoRadEvent(
    correlationId: string,
    eventState: 'start' | 'end',
    appNumber: string,
    user: string,
    status: 'success' | 'error' = null,
    errorDetails: string = null,
    type: 'static' | 'dynamic' = 'dynamic'
  ): void {
    const source = 'workflow'; // we also track this event in the arcDX UI for Post-App resubmit
    const domain = window.location.origin; // TODO: SHOULD BE DEFAULT IN ALL LOGS
    this.logEvent('ecp.ui.shareEligibility', {
      eventState,
      correlationId,
      status,
      appNumber,
      user,
      domain,
      source,
      errorDetails,
      type
    });
  }

  /** filter shares hidden in arcDX Admin */
  private filterHiddenShare(share: Product[]) {
    share
      .filter((f) => !f.isHidden)
      .forEach((f) => {
        f.optionalServices = f.optionalServices.filter((os) => !os.isHidden);
      });
    return share.filter((f) => !f.isHidden);
  }

  /** filter and set applicant shares and services */
  public handlePrimaryApplicant(result: ShareResponse) {
    this.products = this.filterHiddenShare(result.primaryApplicant.shares);
    /** filter services hidden in arcDX Admin */
    result.primaryApplicant.membershipServices = result.primaryApplicant.membershipServices?.filter((s) => !s.isHidden);
    this.membershipServices = result.primaryApplicant.membershipServices
      ? result.primaryApplicant.membershipServices.filter(
          (f) => f.relatedProductTypeName.toLowerCase() === 'other' || f.relatedProductTypeName.toLowerCase() === 'atm/debit card'
        )
      : [];
    const hasCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant');
    /** Set Co Applicant Products */
    if (result.coApplicant.applicantId && hasCoApplicant.value) {
      /** filter services hidden in arcDX Admin */
      result.coApplicant.membershipServices = result.coApplicant.membershipServices?.filter((s) => !s.isHidden);
      const coApplicant = new ApplicantProduct();
      coApplicant.applicantId = result.coApplicant.applicantId;
      coApplicant.firstName = result.coApplicant.firstName;
      coApplicant.lastName = result.coApplicant.lastName;
      coApplicant.bundlePath = 'coApplicant';
      coApplicant.shares = this.filterCoapplicantSharesByPrimary(result.coApplicant.shares);
      coApplicant.membershipServices = result.coApplicant.membershipServices
        ? result.coApplicant.membershipServices.filter(
            (f) => f.relatedProductTypeName.toLowerCase() === 'other' || f.relatedProductTypeName.toLowerCase() === 'atm/debit card'
          )
        : [];
      this.coApplicantProducts.push(coApplicant);
    }

    const hasAdditionalCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasAdditionalCoApplicant');
    /** Set additoinal Co Applicant Products */
    if (result.additionalCoApplicant.applicantId && hasAdditionalCoApplicant.value) {
      /** filter services hidden in arcDX Admin */
      result.additionalCoApplicant.membershipServices = result.additionalCoApplicant.membershipServices?.filter((s) => !s.isHidden);
      const addCoApplicant = new ApplicantProduct();
      addCoApplicant.applicantId = result.additionalCoApplicant.applicantId;
      addCoApplicant.firstName = result.additionalCoApplicant.firstName;
      addCoApplicant.lastName = result.additionalCoApplicant.lastName;
      addCoApplicant.bundlePath = 'additionalCoApplicant';
      addCoApplicant.shares = this.filterCoapplicantSharesByPrimary(result.additionalCoApplicant.shares);
      addCoApplicant.membershipServices = result.additionalCoApplicant.membershipServices
        ? result.additionalCoApplicant.membershipServices.filter(
            (f) => f.relatedProductTypeName.toLowerCase() === 'other' || f.relatedProductTypeName.toLowerCase() === 'atm/debit card'
          )
        : [];
      this.coApplicantProducts.push(addCoApplicant);
    }

    /** Set 4th Applicant Products */
    if (result.responsibleParties4.applicantId) {
      const responParty = new ApplicantProduct();
      responParty.applicantId = result.responsibleParties4.applicantId;
      responParty.firstName = result.responsibleParties4.firstName;
      responParty.lastName = result.responsibleParties4.lastName;
      responParty.bundlePath = 'responsibleParties4';
      responParty.shares = this.filterCoapplicantSharesByPrimary(result.responsibleParties4.shares);
      responParty.membershipServices = result.responsibleParties4.membershipServices
        ? result.responsibleParties4.membershipServices.filter(
            (f) => f.relatedProductTypeName.toLowerCase() === 'other' || f.relatedProductTypeName.toLowerCase() === 'atm/debit card'
          )
        : [];
      this.coApplicantProducts.push(responParty);
    }

    this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').value = result.applicationId;
    this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationStatus').value = result.applicationStatus;
    this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/primaryApplicant/applicantId').value =
      result.primaryApplicant.applicantId;
    this.statefulService.wrap('ss:oao-accounts-and-services').value = JSON.stringify(this.products);
    this.statefulService.wrap('ss:oao-accounts-membership-services').value = JSON.stringify(this.membershipServices);
    this.showAllProduct(true);
    this.groupMembershipServiceByType();
    this.showContactPage = false;
    this.field.data.value = true;
    this.statefulService.wrap(this.COMPLETION_STATUS_PATH).value = true;
    this.enableContinueButton({ page: 'account-and-service', valid: true });
    // this.spinnerInjector.hide();
    // this.isShowHangOnLoading = false;
  }

  /**
   * Processes and updates share options based on the provided ShareResponse.
   * It filters and sets active options for both dividend and maturity categories.
   * Additionally, it determines and sets default values for these options if they exist in active picklist.
   * @param result - The ShareResponse object containing share options and their details.
   */
  public handleShareOptions(result: ShareResponse) {
    this.shareOptions = result.shareOptions;
    /** Set default value */
    const defaultDividendOptions = result.shareOptions.dividendOptions.pickList.filter(
      (t) => t.value === this.shareOptions.dividendOptions.defaultValue
    );
    this.shareOptions.dividendOptions.defaultValue =
      defaultDividendOptions && defaultDividendOptions.length === 1 ? defaultDividendOptions[0].id.toString() : '';

    const defaultMaturityOptions = result.shareOptions.maturityOptions.pickList.filter(
      (t) => t.value === this.shareOptions.maturityOptions.defaultValue
    );
    this.shareOptions.maturityOptions.defaultValue =
      defaultMaturityOptions && defaultMaturityOptions.length === 1 ? defaultMaturityOptions[0].id.toString() : '';
  }

  private filterCoapplicantSharesByPrimary(coapplicantShares: Product[]) {
    const tempCoapplicantShares = [];
    this.products.forEach((e) => {
      const product = coapplicantShares.filter((t) => t.id === e.id)[0];
      if (product) {
        tempCoapplicantShares.push(product);
      }
    });

    return tempCoapplicantShares;
  }

  public displayContactUsPage(): void {
    this.showContactPage = true;
    this.workflowService.currentPage.config.title = this.dictionary.read('accountsAndService/contactUsHeader');
    this.workflowService.currentPage.config.subtitle = this.dictionary.read('accountsAndService/contactUsDescription');
    setTimeout(() => {
      this.dynamicPageService.updateNavState(this.workflowService.currentPage, true);
    });
  }

  /** Make Product list based on product type & sort them accordingly */
  /** Ignoring products with requiredOptInType as opt-out except for Share Certificate and retirement share certificate */
  public groupProductsByType(): void {
    this.products.sort((a, b) => a.sortOrder - b.sortOrder);
    this.groupedProducts = [];
    const count = this.getCountToTrim();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let shortList = this.products.filter((p, i) => !p.isIncluded && p.requiredOptInType !== 'opt-out');
    if (shortList.length > 3 && !this.showFullList) {
      shortList = shortList.slice(0, count);
    }
    /** Here get all the distinct product type name which dont have the isIncluded Property set to true  */
    const groups = new Set(shortList.filter((i) => !i.isIncluded).map((item) => item.type));

    /** look all the product type & make a product list based on each product type */
    groups.forEach((type) => {
      this.groupedProducts.push({
        productType: type.toLocaleLowerCase(),
        sortOrder: shortList.filter((i) => i.type === type)[0]?.sortOrder,
        value: shortList.filter((i) => i.type === type && !i.isIncluded && i.requiredOptInType !== 'opt-out')
      });
    });

    /** Sort the product list based on sort order */
    this.groupedProducts.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /** return no of share to trim first 3 type of share */
  private getCountToTrim() {
    const producta = this.groupSCTerms();
    const count = producta.slice(0, 3).reduce((total, p) => {
      return total + p.value.length;
    }, 0);
    return count;
  }

  /** method return share after grouping the terms */
  private groupSCTerms() {
    const accounts = [];
    this.products
      .filter((p) => !p.isIncluded && p.requiredOptInType !== 'opt-out')
      .forEach((p) => {
        if (SHARE_CERT_TYPE.includes(p.type)) {
          if (!accounts.some((a) => a.groupId === p.shareGroupID)) {
            accounts.push({
              type: p.type,
              value: this.products.filter((a) => a.shareGroupID === p.shareGroupID),
              groupId: p.shareGroupID
            });
          }
        } else {
          accounts.push({
            type: p.type,
            value: [p]
          });
        }
      });
    return accounts;
  }

  /** Make Membership Service list based on product type & sort them accordingly */
  public groupMembershipServiceByType(): void {
    /** Here get all the distinct product type name  which dont have the isIncluded Property set to true  */
    const groups = new Set(
      this.membershipServices
        .filter((i) => !i.isIncluded && i.requiredOptInType.toLowerCase() !== 'opt-out')
        .map((item) => item.relatedProductTypeName)
    );
    /** look all the product type & make a product list based on each product type */
    groups.forEach((type) => {
      this.groupedServices.push({
        productType: type.toLocaleLowerCase(),
        value: this.membershipServices.filter(
          (i) => i.relatedProductTypeName === type && !i.isIncluded && i.requiredOptInType.toLowerCase() !== 'opt-out'
        )
      });
    });
  }

  /** Get only Included Products */
  public getIncludedProducts(): ApplicantProduct {
    const applicantProducts: ApplicantProduct = new ApplicantProduct();
    const showRequiredService = this.adminPortalFeaturesService.config?.oaoFeatures?.canShowRequiredMembershipServices;
    const includedshares = this.products.filter(
      (t) => t.isIncluded && (t.type === 'checking' || t.type === 'saving' || t.type === 'moneymarket' || t.type === 'other')
    );
    applicantProducts.shares = includedshares;
    applicantProducts.membershipServices = this.membershipServices?.filter((t) => showRequiredService && t.isIncluded) || [];
    return applicantProducts;
  }

  /** Get only Optional Products */
  public getOptionalProducts(): ApplicantProduct {
    const applicantProducts: ApplicantProduct = new ApplicantProduct();
    applicantProducts.shares = this.products.filter((m) => m.requiredOptInType.toLowerCase() === 'opt-out');
    applicantProducts.membershipServices = this.membershipServices?.filter((f) => f.requiredOptInType.toLowerCase() === 'opt-out') || [];
    return applicantProducts;
  }

  /** Get Product List based on Product Type */
  public getCoApplicantIncludedProducts(): ApplicantProduct[] {
    return this.coApplicantProducts;
  }

  /** Get Product List based on Product Type */
  public getProductsByType(type: string): Product[] {
    const result = this.groupedProducts.filter((f) => f.productType === type);
    return result && result.length > 0 ? result[0].value : [];
  }

  /** Get Product List based on Product Type */
  public getCoApplicantProductsByType(type: string): ApplicantProduct[] {
    const coAppProducts: ApplicantProduct[] = [];

    this.coApplicantProducts.forEach((item) => {
      const product = new ApplicantProduct();
      product.applicantId = item.applicantId;
      product.firstName = item.firstName;
      product.lastName = item.lastName;
      product.bundlePath = item.bundlePath;
      product.shares = item.shares.filter((f) => f.type === type && !f.isIncluded && f.requiredOptInType !== 'opt-out');
      coAppProducts.push(product);
    });
    return coAppProducts;
  }

  /** Get Membership Service List based on its Type */
  public getServicesByType(type: string): MembershipService[] {
    const result = this.groupedServices.filter((f) => f.productType === type);
    return result && result.length > 0 ? result[0].value : [];
  }

  /** Get Membership Service List based on its Type */
  public getCoApplicantServicesByType(type: string): Product[] {
    const result = this.groupedServices.filter((f) => f.productType === type);
    return result && result.length > 0 ? result[0].value : [];
  }

  /** Get Product List based on Product Type */
  public getCoApplicantMembershipServiceByType(type: string): ApplicantProduct[] {
    const coAppProducts: ApplicantProduct[] = [];
    this.coApplicantProducts.forEach((coAppProduct) => {
      const product = new ApplicantProduct();
      product.applicantId = coAppProduct.applicantId;
      product.firstName = coAppProduct.firstName;
      product.lastName = coAppProduct.lastName;
      product.bundlePath = coAppProduct.bundlePath;
      product.membershipServices = [];
      if (this.membershipServices) {
        const includedMembershipServices = coAppProduct.membershipServices.filter(
          (t) =>
            t.relatedProductTypeName.toLowerCase() === type.toLowerCase() &&
            !t.isIncluded &&
            t.requiredOptInType?.toLowerCase() !== 'opt-out'
        );
        includedMembershipServices.forEach((item) => {
          const service = new MembershipService();
          service.relatedProductName = item.relatedProductName;
          service.description = item.description;
          service.relatedProductId = item.relatedProductId;
          service.availableForJointOwner = item.availableForJointOwner;
          service.isSelected = item.isSelected;
          service.bundleIndex = item.bundleIndex;
          product.membershipServices.push(service);
        });
      }
      coAppProducts.push(product);
    });
    return coAppProducts;
  }

  /** Enable/Disable Continue button  */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public enableContinueButton(obj: any): void {
    if (obj.page.toLowerCase() === 'retirement') {
      this.isRetirementAccountValid = obj.valid;
    }

    if (obj.page.toLowerCase() === 'shared' && !obj.isOptOut) {
      this.isSharedAccountValid = obj.valid;
    }

    if (obj.page.toLowerCase() === 'retirement-shared' && !obj.isOptOut) {
      this.isSharedRetirementValid = obj.valid;
    }
    if (obj.page.toLowerCase() === 'shared' && obj.isOptOut) {
      this.isOptOutSharedAccountValid = obj.valid;
    }

    if (obj.page.toLowerCase() === 'retirement-shared' && obj.isOptOut) {
      this.isOptOutSharedRetirementValid = obj.valid;
    }

    /** To make sure atleast one share is selected */
    this.field.data.value =
      this.isAnyShareSelected &&
      this.isRetirementAccountValid &&
      this.isSharedAccountValid &&
      this.isSharedRetirementValid &&
      this.isOptOutSharedAccountValid &&
      this.isOptOutSharedRetirementValid
        ? 'true'
        : '';
    this.statefulService.wrap(this.COMPLETION_STATUS_PATH).value = this.field.data.value ? true : false;
  }

  /** Set Lender Data */
  public setLenderData(): void {
    this.lenderConfig = this.lenderService.lenderDataInfo;
  }

  public dynamicPageHeader(): void {
    // the below class name declared at ECp Web App.component.html
    const htmlMainTag: HTMLCollection = document.getElementsByClassName(this.PAGE_CONTENT_CLASS_NAME); // TODO: this class doesn't exist?
    const htmlCollection: HTMLCollection = htmlMainTag[0].getElementsByTagName('header');
    const btnContinue = document.getElementById(`go-to-${this.page.nextPage?.path}-button`);
    if (!htmlCollection) {
      return;
    }
    if (this.isShowHangOnLoading) {
      /// Hiding header only if midApp Decision enabled
      if (this.isMidAppDecisionEnabled)
        Array.from(htmlCollection).forEach((element) => {
          element.classList.add('bl-display--none');
        });

      if (btnContinue) {
        btnContinue.style.display = 'none';
      }
    } else {
      Array.from(htmlCollection).forEach((element) => {
        element.classList.remove('bl-display--none');
      });

      if (btnContinue) {
        btnContinue.style.display = 'block';
      }
    }
  }

  /** Method to toggle between  all accounts and first 3 accounts */
  public showAllProduct(isComponentReloaded = false) {
    let shareGroupArr = [];
    let accountsCardState: AdditionalAccountsCardState;
    const stringifyAccountsCardState = this.statefulService.wrap('ss:AdditionalAccountCardState').valueString;
    const bundleAccountsCardState: AdditionalAccountsCardState = {
      isCardSaved: this.statefulService.wrap('api:oaoMemberInformation/additionalAccountCardState/isCardSaved').value as boolean,
      lastSavedState: this.statefulService.wrap('api:oaoMemberInformation/additionalAccountCardState/lastSavedState').value as boolean
    };
    if (bundleAccountsCardState.isCardSaved && bundleAccountsCardState.lastSavedState) {
      accountsCardState = bundleAccountsCardState;
    } else if (stringifyAccountsCardState) {
      accountsCardState = JSON.parse(stringifyAccountsCardState);
    }
    this.showFullList = isComponentReloaded && accountsCardState?.isCardSaved ? accountsCardState?.lastSavedState : !this.showFullList;
    shareGroupArr = this.groupSCTerms();
    this.groupProductsByType();
    if (shareGroupArr.length > 3) {
      this.showmore = this.showFullList ? '' : `Show ${shareGroupArr.length - 3} more accounts`;
    } else {
      this.showmore = '';
    }
    this.statefulService.wrap('ss:isAdditionalAccountCardExpanded').value = this.showFullList;
  }

  initiateProgressBar() {
    // if we don't have progress, set it to 0.
    if (!this.progress) {
      this.progress = 0;
    }

    // if we don't have a total aka no requirement, it's 100%.
    if (this.total === 0) {
      this.total = this.progress;
    } else if (!this.total) {
      this.total = 100;
    }

    // if the progress is greater than the total, it's also 100%.
    if (this.progress > this.total) {
      this.progress = 100;
      this.total = 100;
    }

    const timer$ = interval(2000);

    this.objTimer = timer$.subscribe((sec) => {
      this.progress = (sec * 100) / 100;
      // this.curSec = sec;
      // if (this.curSec === 50) {
      //   this.objTimer.unsubscribe();
      // }
    });
  }

  private handleError(data): void {
    console.error('Error:', data);
  }

  /**
   * Show loading - if streamlined use the new loader and ensure we are far enough in the progress bar,
   * otherwise use the traditional loader.
   */
  private showLoading(): void {
    if (this.workflowService.currentFlow?.config?.streamlined) {
      // use new loader here
      const isPrefill = this.statefulService.wrap(this.workflowService.currentFlow.config.streamlinedPrefillPath).valueString === 'Prefill';
      if (['start', 'next'].includes(this.loaderService.state.type)) {
        this.loaderService.next('Setting things up...');
      } else {
        this.loaderService.start('Setting things up...', {
          overlayMode: 'card',
          animationId: isPrefill ? 'documentscan' : 'default',
          segments: this.workflowService.currentFlow.config.streamlinedSkipPages.length - 1
        });
        for (let i = 0; i < this.workflowService.currentFlow.config.streamlinedSkipPages.length - 1; i++) {
          this.loaderService.next();
        }
      }
    } else {
      this.isShowHangOnLoading = true;
    }
  }

  /**
   * Hide whatever loading mechanism was used.
   */
  private hideLoading(): void {
    if (this.workflowService.currentFlow?.config?.streamlined) {
      // use new loader here
      this.loaderService.end();
    } else {
      this.isShowHangOnLoading = false;
    }
  }
}
